import { Routes } from "@angular/router";
import { ScreenType } from "backend/model/screenType";
import { WorkspaceGuard } from "src/app/guards/workspace.guard";
import { getConflictResolverGuard } from "src/app/workspace/guards/conflict-resolver.guard";
import { AptVersionCanActivateGuard } from "src/standard/guards/can-match/apt-version.can-match-guard";
import { EngagementDetailsReadyGuard } from "src/standard/guards/can-match/engagement-details-ready.guard";
import { EngagementTypeCanActivateGuard } from "src/standard/guards/can-match/engagement-type.can-match-guard";
import { PendingChangesGuard } from "src/standard/guards/pending-changes.guard";
import { StartLoadingQuillDepsGuard } from "src/standard/standard-rich-text-input/quillDependencies";
import { SecurityActivateGuard } from "../../standard/security/security-activate.guard";
import { ModuleEnabledGuard } from "src/app/guards/module-enabled.guard";

export const TEAM_MANAGEMENT_PATH = "team-management";

const routes: Routes = [
  {
    path: "",
    pathMatch: "full",
    loadComponent: () => import("./workspace-home/workspace-home.component"),
    canActivate: [SecurityActivateGuard, WorkspaceGuard],
    data: {
      helpSiteId: "GlobalNavigationMenu",
    },
  },
  {
    path: ":engagementId/access-denied",
    loadComponent: () =>
      import("./workspace-access-denied/workspace-access-denied.component"),
    canActivate: [SecurityActivateGuard],
  },
  {
    path: ":engagementId",
    loadComponent: () =>
      import("./workspace-layout/workspace-layout.component"),
    canActivate: [
      SecurityActivateGuard,
      WorkspaceGuard,
      StartLoadingQuillDepsGuard,
      EngagementDetailsReadyGuard,
    ],
    children: [
      {
        path: "",
        redirectTo: "overview",
        pathMatch: "full",
      },
      {
        path: "node/:nodeId",
        pathMatch: "full",
        loadComponent: () =>
          import("./workspace-node/workspace-node.component"),
        data: {
          helpSiteId: "DocumentManager",
        },
      },
      {
        path: "overview",
        loadComponent: () =>
          import("./workspace-overview/workspace-overview.component"),
        data: {
          helpSiteId: "OverviewScreen",
        },
      },
      {
        path: "execute-gaa-plan/:gaaId/:nodeId",
        loadChildren: () =>
          import("./workspace-general-audit/execute-gaa-plan-list/routes"),
      },
      {
        path: "enter-gaa-plan",
        loadChildren: () =>
          import("./workspace-general-audit/enter-gaa-plan-list/routes"),
        data: {
          helpSiteId: "EnterGAA",
        },
      },
      {
        path: "manage-accounts",
        loadChildren: () => import("./workspace-trial-balance/routes"),
        canActivate: [getConflictResolverGuard(ScreenType.TrialBalanceTool)],
      },
      {
        path: "manage-fsas",
        loadChildren: () => import("./workspace-fsa/routes"),
      },
      {
        path: "manage-groups",
        loadChildren: () => import("./workspace-manage-groups/routes"),
      },
      {
        path: "manage-lead-sheets",
        loadChildren: () => import("./workspace-lead-sheet/routes"),
        canActivate: [getConflictResolverGuard(ScreenType.LeadSheets)],
      },
      {
        path: "custom-report",
        loadChildren: () =>
          import("./workspace-trial-balance-reports/custom-report/routes"),
      },
      {
        path: "fsa-review-plan",
        loadChildren: () => import("./workspace-fsa-review-plan/routes"),
      },
      {
        path: "report",
        loadChildren: () => import("./workspace-trial-balance-reports/routes"),
      },
      {
        path: "scope-in-fsas-review",
        loadChildren: () => import("./workspace-scope-in-fsas-review/routes"),
        canActivate: [EngagementTypeCanActivateGuard(["Review"])],
      },
      {
        path: "scope-in-fsas-audit",
        loadChildren: () =>
          import(
            "./workspace-scope-in-fsas-review/scope-in-fsas-review-grid-3x/routes"
          ),
        canActivate: [
          EngagementTypeCanActivateGuard(["Audit", "Micro Audit"]),
          AptVersionCanActivateGuard(4, "LessThan"),
        ],
        data: {
          helpSiteId: "ScopeInFSA",
        },
      },
      {
        path: "fsa-and-cycles",
        loadChildren: () => import("./workspace-fsa-and-cycles/routes"),
      },
      {
        path: "reporting",
        loadChildren: () => import("./workspace-reporting/reporting/routes"),
        data: {
          helpSiteId: "Reports",
        },
      },
      {
        path: "bulk-edit",
        loadComponent: () =>
          import(
            "./workspace-trial-balance/trial-balance-bulk-edit/trial-balance-bulk-edit.component"
          ),
        data: {
          helpSiteId: "TrialBalanceTool",
        },
      },
      {
        path: "entity",
        loadComponent: () =>
          import(
            "./workspace-trial-balance/trial-balance-entity/trial-balance-entity.component"
          ),
        canActivate: [SecurityActivateGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          helpSiteId: "TrialBalanceTool",
        },
      },
      {
        path: "risk",
        loadChildren: () => import("./workspace-risk/routes"),
        data: {
          helpSiteId: "RiskAndResponse",
        },
        canActivate: [
          ModuleEnabledGuard(ScreenType.RiskandResponseScreen),
          SecurityActivateGuard,
          getConflictResolverGuard(ScreenType.RiskandResponseScreen),
        ],
      },
      {
        path: "control",
        loadChildren: () => import("./workspace-control/routes"),
        data: {
          helpSiteId: "EvaluateControls",
        },
        canActivate: [
          ModuleEnabledGuard(ScreenType.EvaluateControlsScreen),
          getConflictResolverGuard(ScreenType.EvaluateControlsScreen),
          AptVersionCanActivateGuard(4, "GreaterThanOrEqual"),
        ],
      },
      {
        path: "questionnaire/:documentId",
        loadChildren: () => import("./workspace-questionnaire/routes"),
        data: {
          helpSiteId: "Questionnaires",
        },
        canActivate: [
          getConflictResolverGuard(ScreenType.StructureQuestionnaire),
        ],
      },
      {
        path: "questionnaire/:documentId/:questionId",
        loadChildren: () => import("./workspace-questionnaire/routes"),
        data: {
          helpSiteId: "Questionnaires",
        },
        canActivate: [
          getConflictResolverGuard(ScreenType.StructureQuestionnaire),
        ],
      },
      {
        path: "point",
        loadChildren: () => import("./workspace-point/routes"),
        data: {
          //Help Site Id is empty string in APT for Points. So leave it blank
        },
        canActivate: [
          ModuleEnabledGuard(ScreenType.Points),
          getConflictResolverGuard(ScreenType.Points),
        ],
      },
      {
        path: "set-materiality",
        loadChildren: () => import("./workspace-materiality/routes"),
      },
      {
        path: "component-mapping",
        loadChildren: () => import("./workspace-components/routes"),
      },
      {
        path: "fsa-working",
        loadChildren: () => import("./workspace-fsa-working/routes"),
      },
      {
        path: "review-point",
        loadChildren: () => import("./workspace-review-point/routes"),
        data: {},
      },
      {
        path: "warning",
        loadChildren: () => import("./workspace-warning/routes"),
        data: {
          helpSiteId: "Warnings",
        },
      },
      {
        path: "conflict",
        loadChildren: () => import("./workspace-conflict/routes"),
        data: {},
      },
      {
        path: TEAM_MANAGEMENT_PATH,
        loadChildren: () => import("./workspace-team-management/routes"),
        data: {
          helpSiteId: "TeamManagement",
        },
      },
      {
        path: "summary-ssp-residuals-test-plan",
        loadChildren: () =>
          import("./workspace-summary-ssp-residuals-test-plan/routes"),
        data: {
          helpSiteId: "SummarySSPPlan",
        },
        canActivate: [
          getConflictResolverGuard(
            ScreenType.SummarySupplementarySubstantiveProceduresPlanScreen,
          ),
        ],
      },
      {
        path: "adjusted-journals",
        loadChildren: () => import("../journals/routes"),
        data: {
          helpSiteId: "AdjustedJournals",
        },
        canActivate: [getConflictResolverGuard(ScreenType.AdjustedJournals)],
      },
      {
        path: "unadjusted-journals",
        loadChildren: () => import("../journals/routes"),
        data: {
          helpSiteId: "UnadjustedJournals",
        },
        canActivate: [getConflictResolverGuard(ScreenType.UnadjustedJournals)],
      },
      {
        path: "consolidation-journals",
        loadChildren: () => import("../journals/routes"),
        canActivate: [
          getConflictResolverGuard(ScreenType.ConsolidationJournals),
        ],
      },
      {
        path: "par",
        loadChildren: () => import("./workspace-par/routes"),
        data: {},
        canActivate: [
          getConflictResolverGuard(ScreenType.PreliminaryAnalyticalReview),
        ],
      },
      {
        path: "final-analytical-review",
        loadChildren: () => import("./workspace-far/routes"),
        data: {},
        canActivate: [
          getConflictResolverGuard(ScreenType.FinalAnalyticalReview),
        ],
      },
      {
        path: "entity",
        loadComponent: () =>
          import(
            "./workspace-trial-balance/trial-balance-entity/trial-balance-entity.component"
          ),
        data: {
          helpSiteId: "TrialBalanceTool",
        },
        canActivate: [getConflictResolverGuard(ScreenType.TrialBalanceTool)],
      },
      {
        path: "consolidation-report",
        pathMatch: "full",
        loadComponent: () =>
          import(
            "./workspace-reports/consolidation-report/consolidation-report.component"
          ),
        canActivate: [SecurityActivateGuard],
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: "planning-sign-off",
        loadComponent: () =>
          import(
            "./workspace-planning-signoff/workspace-planning-signoff.component"
          ),
        canDeactivate: [PendingChangesGuard],
        data: {
          helpSiteId: "PlanningSignOff",
        },
      },
      {
        path: "design-gaa-plan",
        loadComponent: () =>
          import(
            "./workspace-general-audit/design-gaa-plan-list/design-gaa-plan-list.component"
          ),
        data: {
          helpSiteId: "DesignGAAAuditPlan",
        },
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: "engagement-sign-off",
        loadComponent: () =>
          import(
            "./workspace-engagement-signoff/workspace-engagement-signoff.component"
          ),
        canDeactivate: [PendingChangesGuard],
        data: {
          helpSiteId: "EngagementSignOff",
        },
      },
      {
        path: "inquiry-tests-review-plan",
        loadChildren: () => import("./workspace-inquiry-review-plan/routes"),
        canActivate: [
          getConflictResolverGuard(ScreenType.InquiryTestsReviewPlan),
        ],
      },
      {
        path: "execute-fsa-audit/:screenId",
        loadComponent: () =>
          import(
            "./workspace-fsa-audit/execute-fsa-audit/execute-fsa-audit.component"
          ),
        canActivate: [getConflictResolverGuard(ScreenType.ExecuteFsaAudit)],
        canDeactivate: [PendingChangesGuard],
        data: {
          helpSiteId: "EvaluateAuditPlan",
        },
      },
      {
        path: "execute-fsa-audit-3x/:screenId",
        loadComponent: () =>
          import(
            "./workspace-fsa-audit/execute-fsa-audit-3x/execute-fsa-audit-3x.component"
          ),
        pathMatch: "full",
        data: {
          helpSiteId: "ExecuteFSAAudit",
        },
        canActivate: [
          SecurityActivateGuard,
          getConflictResolverGuard(ScreenType.ExecuteFsaAudit),
        ],
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: "summary-risk-plan",
        loadChildren: () => import("./workspace-summary-risk-plan/routes"),
        canActivate: [getConflictResolverGuard(ScreenType.SummaryRiskPlan)],
        data: {
          helpSiteId: "SummaryRiskPlan",
        },
      },
      {
        path: "evaluation-of-potential-risks",
        loadChildren: () =>
          import("./workspace-evaluation-of-potential-risks/routes"),
        canActivate: [
          getConflictResolverGuard(ScreenType.EvaluationPotentialRisks),
        ],
        data: {
          helpSiteId: "EvaluationPotentialRisks",
        },
      },
      {
        path: "risk-gnp",
        loadChildren: () =>
          import("./workspace-evaluation-of-potential-risks/routes"),
        canActivate: [
          getConflictResolverGuard(ScreenType.EvaluationPotentialRisks),
        ],
      },
      {
        path: "design-execute-fsa-3x/:screenId",
        loadComponent: () =>
          import(
            "./workspace-fsa-audit/design-execute-fsa-3x/design-execute-fsa-3x.component"
          ),
        data: {
          helpSiteId: "DesignExecuteFSAAuditPlan",
        },
        canActivate: [
          SecurityActivateGuard,
          getConflictResolverGuard(ScreenType.DesignAndExecuteFsaAudit),
        ],
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: "recycle-bin",
        loadChildren: () => import("./workspace-layout/recycle-bin/routes"),
      },
      {
        path: "enter-fsa",
        loadChildren: () => import("./workspace-enter-fsa/routes"),
      },
      {
        path: "design-fsa-audit-3x/:screenId",
        loadComponent: () =>
          import(
            "./workspace-fsa-audit/design-fsa-audit-3x/design-fsa-audit-3x.component"
          ),
        data: {
          helpSiteId: "DesignFSAAudit",
        },
        canActivate: [
          SecurityActivateGuard,
          getConflictResolverGuard(ScreenType.DesignFsaAudit),
        ],
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: "**",
        loadComponent: () =>
          import(
            "src/standard/pages/error-pages/page-not-found/page-not-found.component"
          ),
      },
    ],
  },
];

export default routes;
